'use es6';

import debounce from 'react-utils/debounce';
import { EDIT_TABLE } from '../constants/usageTracking';
import { trackInteraction } from '../utils/usageTracking';
import { getCommonValue, getDimension, getLayout, getSelectedTableNode, getStyle, getTextAlign, getVTextAlign, isResponsiveTableNode, isTableNode } from '../utils/table';
import { getMargins } from '../utils/shared';
const debouncedTrackInteraction = debounce(trackInteraction, 400);
export const trackEditTableStyle = (editor, editType) => {
  debouncedTrackInteraction(editor, 'tinymceEditTable', EDIT_TABLE, {
    version: 'contextualToolbar',
    editType
  });
};
export function getTableAttributes(editor, tableNode) {
  function getAttr(name) {
    return tableNode ? tableNode.getAttribute(name) : '';
  }
  function getCellStyle(name) {
    const cells = editor.dom.select('td,th', tableNode);
    let commonStyle;
    for (let i = 0; i < cells.length; i++) {
      const cellStyle = cells[i] && cells[i].style ? cells[i].style[name] : null;
      if (!commonStyle) {
        commonStyle = cellStyle;
      }
      if (commonStyle !== cellStyle) {
        return '';
      }
    }
    return commonStyle;
  }
  const tableIsResponsive = isResponsiveTableNode(tableNode);
  const tableWidth = tableIsResponsive ? getStyle(tableNode.parentNode, 'width') : getStyle(tableNode, 'width') || getAttr('width') || tableNode.offsetWidth;
  const tableHeight = getStyle(tableNode, 'height') || getAttr('height') || tableNode.offsetHeight;
  const tableCellPadding = getAttr('data-mce-cell-padding') || getAttr('cellpadding') || getCellStyle('padding') || '0';
  const tableBorderWidth = getAttr('data-mce-border') || getAttr('border') || getStyle(tableNode, 'borderWidth') || '0';
  const tableBorderColor = getAttr('data-mce-border-color') || getStyle(tableNode, 'border-color');
  const tableBorderStyle = getStyle(tableNode, 'border-style') || 'solid';
  const tableCaption = !!editor.dom.select('caption', tableNode)[0];
  const tableLayout = getLayout(editor, tableNode);
  const tableStyle = editor.dom.serializeStyle(editor.dom.parseStyle(getAttr('style')));
  return {
    tableWidth,
    tablePixelWidth: tableNode.offsetWidth,
    tableHeight,
    dimensions: {
      width: tableWidth,
      height: tableHeight
    },
    alignment: tableLayout,
    tableCellPadding: parseInt(tableCellPadding, 10),
    tableBorderWidth: parseInt(tableBorderWidth, 10),
    borderColor: tableBorderColor ? {
      color: editor.dom.styles.toHex(tableBorderColor),
      alpha: 100
    } : tableBorderColor,
    tableBorderStyle,
    tableCaption,
    tableIsResponsive,
    tableStyle,
    margin: getMargins(editor, tableIsResponsive ? tableNode.parentNode : tableNode)
  };
}
export function getTableCellAttributes(editor, cellNodes = []) {
  const backgroundColor = getCommonValue(cellNodes, node => getStyle(node, 'background-color'), '');
  const borderColor = getCommonValue(cellNodes, node => getStyle(node, 'border-color'), '');
  const cellBorderWidth = getCommonValue(cellNodes, node => getStyle(node, 'border-width', '0'), 0);
  const cellBorderStyle = getCommonValue(cellNodes, node => getStyle(node, 'border-style', 'none'), 'none');
  const cellHorizontalAlignment = getCommonValue(cellNodes, node => getTextAlign(editor, node), null);
  const verticalAlignment = getCommonValue(cellNodes, node => getVTextAlign(editor, node), 'middle');
  const cellWidth = getCommonValue(cellNodes, node => getDimension(node, 'width'), '');
  const cellHeight = getCommonValue(cellNodes, node => getDimension(node, 'height'), '');
  const tableNode = getSelectedTableNode(editor, cellNodes[0]);
  const tableWidth = isResponsiveTableNode(tableNode) ? getStyle(tableNode.parentNode, 'width') : getStyle(tableNode, 'width') || tableNode.getAttribute('width') || tableNode.offsetWidth;
  return {
    fillColor: backgroundColor ? {
      color: editor.dom.styles.toHex(backgroundColor),
      alpha: 100
    } : {},
    borderColor: borderColor ? {
      color: editor.dom.styles.toHex(borderColor),
      alpha: 100
    } : {},
    cellBorderWidth,
    cellBorderStyle,
    cellHorizontalAlignment,
    cellWidth: cellWidth && parseInt(cellWidth, 10),
    cellHeight: cellHeight && parseInt(cellHeight, 10),
    verticalAlignment,
    tableWidth
  };
}
export function getTableCellMergeSplitProperties(editor, cellNodes) {
  const selectedCellsSpanColumns = cellNodes.every(cell => !!editor.dom.getAttrib(cell, 'colspan') && parseInt(editor.dom.getAttrib(cell, 'colspan'), 10) > 1);
  const selectedCellsSpanRows = cellNodes.every(cell => !!editor.dom.getAttrib(cell, 'rowspan') && parseInt(editor.dom.getAttrib(cell, 'rowspan'), 10) > 1);
  const canMergeTableCells = cellNodes.length > 1;
  const canSplitTableCells = selectedCellsSpanColumns || selectedCellsSpanRows;

  // Find out how many rows & cols are spanned
  const rowIndices = {};
  const colIndices = {};
  cellNodes.forEach(cell => {
    const parentRow = cell.parentElement;
    const rowIdx = Array.from(parentRow.parentElement.childNodes).indexOf(parentRow);
    const colIdx = Array.from(parentRow.childNodes).indexOf(cell);
    rowIndices[rowIdx] = cell;
    colIndices[colIdx] = cell;
  });
  const cellsSpanRowsCount = Object.keys(rowIndices).length;
  const cellsSpanColsCount = Object.keys(colIndices).length;
  const firstRowIdx = Object.keys(rowIndices).sort()[0];
  return {
    showMerge: canMergeTableCells,
    showSplit: canSplitTableCells,
    cellsSpanRowsCount,
    cellsSpanColsCount,
    // We always want the top-right most cell, which will be the last cell
    // added to the first row index
    targetCell: rowIndices[firstRowIdx]
  };
}

// `tableNodes` is an array of cells or an array containing just the table node
export const setTableStyles = (editor, tableNodes = [], styles = {}) => {
  editor.undoManager.transact(() => {
    tableNodes.forEach(node => {
      const style = editor.dom.serializeStyle(Object.assign({}, editor.dom.parseStyle(editor.dom.getAttrib(node, 'style')), styles));
      editor.dom.setAttribs(node, {
        style
      });
    });
  });
  const prefix = isTableNode(tableNodes[0]) ? 'table' : 'cell';
  const editType = `${prefix}-${Object.keys(styles)[0]}`;
  trackEditTableStyle(editor, editType);
};